// H-003 — lista cronológica (de la más nueva a la más vieja) de las versiones de UN recurso: versión, fecha del servidor, autor y qué campos cambiaron.
// Solo lectura. Todo pasa por el proxy del servidor (`GET <Tipo>/<id>/_history` y `GET Provenance?target=`), con las mismas guardas que leer el recurso
// (vínculo asistencial, AccessPolicy del rol, bitácora de acceso). Si el servidor niega el acceso, no se muestra nada del recurso.
// Del contenido solo se muestran NOMBRES de campos (ver lib/historialVersiones.ts): ningún valor de una versión anterior.
import { useMedplum } from '@medplum/react-hooks';
import type { Bundle, Provenance, ResourceType } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { tieneSiguiente, versionesDeHistorial, type VersionRecurso } from '@/lib/historialVersiones';
import { accesoDenegadoDe } from './accesoDenegado';
import { TablaVacia } from './TablaVacia';

/** Tamaño de página y topes: hasta 1000 versiones y 500 Provenance. Más allá se avisa que el historial está truncado. */
export const POR_PAGINA = 100;
export const PAGINAS_MAXIMAS_VERSIONES = 10;
export const PAGINAS_MAXIMAS_PROVENANCE = 5;

/** Fecha y hora en la zona del consultorio (Ecuador continental), no la del navegador. */
export const fechaEcuador = (iso: string): string => new Date(iso).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' });

type Estado = { tipo: 'cargando' } | { tipo: 'ok'; versiones: VersionRecurso[]; truncado: boolean } | { tipo: 'denegado' } | { tipo: 'error' };

export function HistorialVersiones({ tipo, id }: { tipo: ResourceType; id: string }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado>({ tipo: 'cargando' });

  useEffect(() => {
    let vigente = true;
    setEstado({ tipo: 'cargando' });
    (async () => {
      try {
        // Página a página (`link next`) hasta el tope; sin caché: el historial tiene que ser el del servidor ahora.
        const paginas: Bundle[] = [];
        let quedanVersiones = true;
        for (let i = 0; i < PAGINAS_MAXIMAS_VERSIONES && quedanVersiones; i++) {
          const pagina = await medplum.readHistory(tipo, id, { count: POR_PAGINA, offset: i * POR_PAGINA }, { cache: 'no-cache' });
          paginas.push(pagina);
          quedanVersiones = tieneSiguiente(pagina);
        }
        // El Provenance es complementario (autor de una adenda): si falla, se sigue con `meta.author`.
        const provenances: Provenance[] = [];
        let quedanProvenance = true;
        try {
          for (let i = 0; i < PAGINAS_MAXIMAS_PROVENANCE && quedanProvenance; i++) {
            const pagina = await medplum.search('Provenance', { target: `${tipo}/${id}`, _count: POR_PAGINA, _offset: i * POR_PAGINA });
            for (const e of pagina.entry ?? []) if (e.resource) provenances.push(e.resource);
            quedanProvenance = tieneSiguiente(pagina);
          }
        } catch {
          quedanProvenance = false;
        }
        if (vigente) {
          setEstado({ tipo: 'ok', versiones: versionesDeHistorial(tipo, id, paginas, provenances, { completo: !quedanVersiones }), truncado: quedanVersiones || quedanProvenance });
        }
      } catch (err) {
        if (!vigente) return;
        const status = (err as { outcome?: { issue?: Array<{ code?: string }> } } | null)?.outcome?.issue?.[0]?.code;
        setEstado({ tipo: accesoDenegadoDe(err) || status === 'forbidden' ? 'denegado' : 'error' });
      }
    })();
    return () => {
      vigente = false;
    };
  }, [medplum, tipo, id]);

  if (estado.tipo === 'cargando') return <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>;
  if (estado.tipo === 'denegado') return <p role="alert" className="text-sm text-destructive">{t('historialCambios.denegado')}</p>;
  if (estado.tipo === 'error') return <p role="alert" className="text-sm text-destructive">{t('historialCambios.error')}</p>;
  if (estado.versiones.length === 0) return <TablaVacia mensaje={t('historialCambios.sinVersiones')} />;

  const describirCambio = (v: VersionRecurso): string => {
    if (v.eliminado) return t('historialCambios.eliminado');
    if (v.creacion) return t('historialCambios.creacion');
    if (v.camposCambiados === null) return t('historialCambios.sinVersionAnterior');
    if (v.camposCambiados.length === 0) return t('historialCambios.sinCambiosDeContenido');
    return v.camposCambiados.map((c) => t(`historialCambios.campos.${c}`, c)).join(', ');
  };

  return (
    <>
    {estado.truncado && <p role="note" className="text-xs text-muted-foreground">{t('historialCambios.truncado')}</p>}
    <Table aria-label={t('historialCambios.tablaVersiones')}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('historialCambios.columnas.version')}</TableHead>
          <TableHead>{t('historialCambios.columnas.fecha')}</TableHead>
          <TableHead>{t('historialCambios.columnas.autor')}</TableHead>
          <TableHead>{t('historialCambios.columnas.cambios')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {estado.versiones.map((v, i) => (
          <TableRow key={`${v.versionId}-${i}`}>
            <TableCell className="font-mono text-xs">{v.versionId}</TableCell>
            <TableCell>
              {v.fecha ? (
                <time dateTime={v.fecha}>{fechaEcuador(v.fecha)}</time>
              ) : (
                '—'
              )}
            </TableCell>
            <TableCell>{v.autor ?? t('historialCambios.autorDesconocido')}</TableCell>
            <TableCell>
              {describirCambio(v)}
              {v.motivo && <span className="block text-xs text-muted-foreground">{t('historialCambios.motivo', { motivo: v.motivo })}</span>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </>
  );
}
